import { useState } from 'react'
import type { JSX } from 'react'
import { useMatch } from 'react-router-dom'

import { PageHeader } from '@buffer-mono/popcorn'

import { ViewOnlyBadge } from '~publish/components/ViewOnlyBadge'
import { ViewOnlyPublishCoachMark } from '~publish/components/ViewOnlyCoachMarks'
import type { ViewOnlyCopy } from '~publish/components/ViewOnlyCoachMarks'
import {
  useCurrentOrganization,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'
import { CreatePrimaryAction } from '~publish/pages/Create/CreateHeaderSlots'

import { AddFeedDropdownMenu } from './components/AddFeedDropdownMenu'
import { FeedsChipsList } from './components/FeedsChipsList'
import { useFeedsNavigation, useParsedFeedsData } from './hooks'

import styles from './FeedsNavigation.module.css'

const VIEW_ONLY_FEEDS_COPY: ViewOnlyCopy = {
  title: 'You have view-only access to Feeds',
  body:
    'You can read the feeds your organization follows. To add, move, or ' +
    'delete feeds, ask an admin to update your permissions.',
}

export function FeedsNavigation(): JSX.Element | null {
  const isFeedsRoute = useMatch('/create/feeds?/*')
  const organization = useCurrentOrganization()
  const canManageFeeds = useHasOrganizationAction('manageFeeds')

  const [isAddingCollection, setIsAddingCollection] = useState(false)

  const { collections, feeds, existingFeeds, totalFeeds, error } =
    useParsedFeedsData(organization?.id)

  const {
    tier,
    limitReached,
    collectionsLimitReached,
    totalRemainingFeeds,
    handleCreateFeed,
    handleDeleteFeed,
    handleCreateCollection,
    handleUpgradeClick,
  } = useFeedsNavigation({
    collectionsCount: collections.length,
    totalFeeds,
  })

  const onCreateCollection = async (name: string): Promise<void> => {
    await handleCreateCollection(name)
    setIsAddingCollection(false)
  }

  if (!isFeedsRoute) return null
  if (error) return null

  // Hide navigation when there are no feeds or collections
  const hasNoFeeds = feeds.length === 0 && collections.length === 0
  if (hasNoFeeds) return null

  return (
    <>
      <CreatePrimaryAction>
        {canManageFeeds ? (
          <AddFeedDropdownMenu
            tier={tier}
            limitReached={limitReached}
            collectionsLimitReached={collectionsLimitReached}
            totalRemainingFeeds={totalRemainingFeeds}
            existingFeeds={existingFeeds}
            onAddFeed={handleCreateFeed}
            onDeleteFeed={handleDeleteFeed}
            onUpgrade={handleUpgradeClick}
            onAddCollection={(): void => setIsAddingCollection(true)}
          />
        ) : (
          <ViewOnlyPublishCoachMark copy={VIEW_ONLY_FEEDS_COPY}>
            <ViewOnlyBadge />
          </ViewOnlyPublishCoachMark>
        )}
      </CreatePrimaryAction>
      <PageHeader.Row className={styles.row}>
        <FeedsChipsList
          feeds={feeds}
          collections={collections}
          isAddingCollection={isAddingCollection}
          onCreateCollection={onCreateCollection}
          onCancelCollection={(): void => setIsAddingCollection(false)}
        />
      </PageHeader.Row>
    </>
  )
}
